import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import ProgressCard from 'components/charts/ProgressCard';
import PieChartV2 from 'components/charts/chartJS/PieChart';
import { chartColors } from 'components/charts/chartJS/utils';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import EnumSelect from 'components/field/EnumSelect';
import Card from 'components/global/Card';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { AUTOCOMPLETE, ENUMS, ORDERS } from 'services/consts';

import PATHS, { mergePaths } from 'utils/paths';

import * as S from './BulletTracking.styled';

const FILTER_LIST = {
	deliveryCompanyName: {
		key: 'deliveryCompanyName',
		value: ''
	},
	agreementType: {
		key: 'agreementType',
		value: ''
	},
	companyId: {
		key: 'companyId',
		value: ''
	}
};

const AllFilters = ({ setFilter, pageFilter }) => {
	const [filters, setFilters] = useState(JSON.parse(JSON.stringify(FILTER_LIST)));
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);

	const changeFilter = (key, value) => {
		setFilters((prev) => ({
			...prev,
			[key]: {
				...prev[key],
				value
			}
		}));
	};

	const applyAllFilters = (closePopper) => {
		setFilter(filters);
		closePopper?.();
	};

	return (
		<>
			<Button
				id="filters-button"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
				ref={setFilterMenuButtonRef}
				variant="borderless"
				label="allFilters"
			/>

			{filtersMenuButtonRef && (
				<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
					{(closePopper) => (
						<S.PopperMenu>
							<S.PopperMenuItem>
								<Typography variant="p2" fontWeight="500">
									deliveryCompany
								</Typography>
								<EnumSelect
									enumName={ENUMS.DELIVERY_COMPANIES}
									value={pageFilter?.deliveryCompanyName}
									setValue={(value) => changeFilter('deliveryCompanyName', value || null)}
								/>
							</S.PopperMenuItem>

							<S.PopperMenuItem>
								<Typography variant="p2" fontWeight="500">
									agreementType
								</Typography>
								<EnumSelect
									data={['AGGREGATOR', 'OMNI_AGGREGATOR']}
									value={pageFilter?.agreementType}
									setValue={(value) => changeFilter('agreementType', value || null)}
								/>
							</S.PopperMenuItem>

							<S.PopperMenuItem>
								<Typography variant="p2" fontWeight="500">
									clientName
								</Typography>
								<AutoComplete
									autoCompleteName={AUTOCOMPLETE.CLIENTS}
									value={pageFilter?.companyId}
									setValue={(value) => {
										changeFilter('companyId', value ? value?.ID : null);
									}}
									columns={[
										{ accessor: 'ID', width: 100 },
										{ accessor: 'Name', width: 400 }
									]}
									selectionLabelAccessor={['ID', 'Name']}
									fixedContainerWidth={false}
								/>
							</S.PopperMenuItem>
							<S.PopperMenuItem className="sticky">
								<Button
									variant="action"
									label="applyFilter"
									size="small"
									onClick={() => applyAllFilters(closePopper)}
								/>
							</S.PopperMenuItem>
						</S.PopperMenu>
					)}
				</Popper>
			)}
		</>
	);
};

const OverView = () => {
	const [filterList, setFilterList] = useState(JSON.parse(JSON.stringify(FILTER_LIST)));

	const commonFilter = useMemo(
		() =>
			Object.entries(filterList).reduce(
				(acc, [key, { value }]) => (value && value.length !== 0 ? { ...acc, [key]: value } : acc),
				{}
			),
		[filterList]
	);

	return (
		<S.Container>
			<AllFilters setFilter={setFilterList} pageFilter={commonFilter} />
			<div className={'w-100'}>
				<StatusDistribution commonFilter={commonFilter} />
			</div>
			<S.PieChartWrapper>
				<CompletedShipments commonFilter={commonFilter} />
			</S.PieChartWrapper>
			<S.PieChartWrapper>
				<NotCompletedShipments commonFilter={commonFilter} />
			</S.PieChartWrapper>
			<S.PieChartWrapper>
				<DeliveryCompany commonFilter={commonFilter} />
			</S.PieChartWrapper>
			<div className={'w-100'}>
				<SlaPerformance commonFilter={commonFilter} />
			</div>
		</S.Container>
	);
};

const StatusDistribution = ({ commonFilter }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const navigate = useNavigate();

	const body = useMemo(
		() => ({ from: filter.start, to: filter.end, totalRecord: false, ...commonFilter }),
		[filter, commonFilter]
	);

	const handleButtonClick = (header, accessor, value) => {
		navigate(mergePaths(PATHS.BULLET_TRACKING, PATHS.BULLET_TRACKING_LIST), {
			state: {
				defaultFilters: [
					{
						column: {
							Header: header,
							accessor,
							filterType: FilterType.string
						},
						action: FilterAction.is,
						value
					},
					{
						column: {
							Header: 'agreementType',
							accessor: 'agreementType',
							filterType: FilterType.enumMulti
						},
						action: FilterAction.contains,
						value: commonFilter?.agreementType
							? [commonFilter?.agreementType]
							: ['AGGREGATOR', 'OMNI_AGGREGATOR']
					},
					...(commonFilter?.deliveryCompanyName
						? [
								{
									column: {
										Header: 'deliveryCompany',
										accessor: 'deliveryCompanyName',
										filterType: FilterType.string
									},
									action: FilterAction.startsWith,
									value: commonFilter.deliveryCompanyName
								}
							]
						: []),
					...(commonFilter?.companyId
						? [
								{
									column: {
										Header: 'companyId',
										accessor: 'companyId',
										filterType: FilterType.number
									},
									action: FilterAction.is,
									value: commonFilter.companyId
								}
							]
						: []),
					{
						column: {
							Header: 'shipmentCreatedDate',
							accessor: 'shipmentCreatedDate',
							filterType: FilterType.date
						},
						action: FilterAction.between,
						...(body && {
							value: {
								start: body.from,
								end: body.to
							}
						})
					}
				]
			}
		});
	};

	const cardsConfig = [
		{
			accessor: 'total',
			label: 'total',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => {
				navigate(mergePaths(PATHS.BULLET_TRACKING, PATHS.BULLET_TRACKING_LIST), {
					state: {
						defaultFilters: [
							{
								column: {
									Header: 'agreementType',
									accessor: 'agreementType',
									filterType: FilterType.enumMulti
								},
								action: FilterAction.contains,
								value: commonFilter?.agreementType
									? [commonFilter?.agreementType]
									: ['AGGREGATOR', 'OMNI_AGGREGATOR']
							},
							...(commonFilter?.deliveryCompanyName
								? [
										{
											column: {
												Header: 'deliveryCompany',
												accessor: 'deliveryCompanyName',
												filterType: FilterType.string
											},
											action: FilterAction.startsWith,
											value: commonFilter.deliveryCompanyName
										}
									]
								: []),
							...(commonFilter?.companyId
								? [
										{
											column: {
												Header: 'companyId',
												accessor: 'companyId',
												filterType: FilterType.number
											},
											action: FilterAction.is,
											value: commonFilter.companyId
										}
									]
								: []),
							{
								column: {
									Header: 'shipmentCreatedDate',
									accessor: 'shipmentCreatedDate',
									filterType: FilterType.date
								},
								action: FilterAction.between,
								...(body && {
									value: {
										start: body.from,
										end: body.to
									}
								})
							}
						]
					}
				});
			}
		},
		{
			accessor: 'shipmentCreated',
			label: 'shipmentCreated',
			customIcon: 'fa-regular fa-ship',
			backgroundColor: 'var(--fa-regular fa-ship-bgColor)',
			color: 'var(--fa-regular fa-ship-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['searchingDriver', 'shipmentCreated'])
		},
		{
			accessor: 'pickedUp',
			label: 'pickedUp',
			customIcon: 'fa-regular fa-person-carry-box',
			backgroundColor: 'var(--fa-regular fa-person-carry-box-bgColor)',
			color: 'var(--fa-regular fa-person-carry-box-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['pickedUp'])
		},
		{
			accessor: 'goingToPickup',
			label: 'goingToPickup',
			customIcon: 'fa-regular fa-truck',
			backgroundColor: 'var(--fa-regular fa-truck-bgColor)',
			color: 'var(--fa-regular fa-truck-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['goingToPickup', 'arrivedPickup'])
		},
		{
			accessor: 'outForDelivery',
			label: 'outForDelivery',
			customIcon: 'fa-solid fa-truck-fast',
			backgroundColor: 'var(--fa-solid fa-truck-fast-bgColor)',
			color: 'var(--fa-solid fa-truck-fast-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['outForDelivery', 'arrivedDestination'])
		},
		{
			accessor: 'undeliveredAttempt',
			label: 'undeliveredAttempt',
			customIcon: 'fa-regular-box-circle-xmark',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['undeliveredAttempt'])
		},
		{
			accessor: 'returnShipmentProcessing',
			label: 'returnShipmentProcessing',
			customIcon: 'fa-regular fa-ship',
			backgroundColor: 'var(--fa-regular fa-ship-bgColor)',
			color: 'var(--fa-regular fa-ship-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['returnShipmentProcessing'])
		},
		{
			accessor: 'pickupSLASuccess',
			label: 'pickupSLASuccess',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleButtonClick('pickupSLASuccess', 'pickupSLASuccess', ['yes'])
		},
		{
			accessor: 'pickupSLABreach',
			label: 'pickupSLABreach',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleButtonClick('pickupSLASuccess', 'pickupSLASuccess', ['no'])
		},
		{
			accessor: 'deliverySLASuccess',
			label: 'deliverySLASuccess',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleButtonClick('deliverySLASuccess', 'deliverySLASuccess', ['yes'])
		},
		{
			accessor: 'deliverySLABreach',
			label: 'deliverySLABreach',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => handleButtonClick('deliverySLASuccess', 'deliverySLASuccess', ['no'])
		},
		{
			accessor: 'shipmentCanceled',
			label: 'shipmentCanceled',
			customIcon: 'fa-regular-box-circle-xmark',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['shipmentCanceled'])
		},
		{
			accessor: 'delivered',
			label: 'delivered',
			customIcon: 'fa-regular-box-circle-check',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['delivered'])
		},
		{
			accessor: 'returned',
			label: 'returned',
			customIcon: 'fa-regular-box-circle-arrow-left',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)',
			onButtonClick: () => handleButtonClick('status', 'status', ['returned', 'confirmedReturn'])
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: ORDERS.GET_BULLET_SHIPMENT_NUMBERS,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader
				title="statusDistribution"
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

const pieChartAfterFetch = (data) => {
	const labels = data.map((item) => item.label);
	const datasets = [{ data: data.map((item) => item.value), ...chartColors }];
	return { labels, datasets };
};

const CompletedShipments = ({ commonFilter }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(
		() => ({ from: filter.start, to: filter.end, totalRecord: false, ...commonFilter }),
		[filter, commonFilter]
	);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: ORDERS.GET_BULLET_COMPLETED_BULLET_ORDERS,
		body,
		parseData: pieChartAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'completedShipments'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

const NotCompletedShipments = ({ commonFilter }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(
		() => ({ from: filter.start, to: filter.end, totalRecord: false, ...commonFilter }),
		[filter, commonFilter]
	);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: ORDERS.GET_BULLET_NOT_COMPLETED_BULLET_ORDERS,
		body,
		parseData: pieChartAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'notCompletedShipments'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

const DeliveryCompany = ({ commonFilter }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(
		() => ({ from: filter.start, to: filter.end, totalRecord: false, ...commonFilter }),
		[filter, commonFilter]
	);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: ORDERS.GET_BULLET_ORDERS_DC_LIST,
		body,
		parseData: pieChartAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={'deliveryCompany'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

const SlaPerformance = ({ commonFilter }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(
		() => ({ from: filter.start, to: filter.end, totalRecord: false, ...commonFilter }),
		[filter, commonFilter]
	);

	const progressCardConfig = [
		{
			timeLabel: 'averagePickupTime',
			time: 'avgPickupTime',
			percentageLabel: 'pickupSLACompliance',
			percentage: 'pickupSlaPercentage',
			color: '#2196f3'
		},
		{
			timeLabel: 'averageDeliveryTime',
			time: 'avgTotalDeliveryTime',
			percentageLabel: 'deliverySLACompliance',
			percentage: 'deliverySlaPercentage',
			color: '#43a047'
		}
	];

	return (
		<>
			<ChartHeader
				title={'slaPerformance'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<ProgressCard
				fetchURL={ORDERS.GET_BULLET_PERFORMANCE}
				loadingCardNumber={2}
				dataConfig={progressCardConfig}
				body={body}
			/>
		</>
	);
};

export default OverView;
